import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, Tab, Tabs, Alert, Grid
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add, Warning } from '@mui/icons-material';
import { useLoteStore } from '../store/loteStore';
import { loteService } from '../services/loteService';
import { type Lote, initialLoteState } from '../types/lote.types';

const LotesPage = () => {
  const { lotes, loading, setLotes, setLoading } = useLoteStore();
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<Lote>(initialLoteState);
  const [proximosVencer, setProximosVencer] = useState<Lote[]>([]);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => { loadData(); loadProximosVencer(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { setLotes(await loteService.getAll()); } finally { setLoading(false); }
  };

  const loadProximosVencer = async () => {
    try { setProximosVencer(await loteService.getProximosAVencer(30)); } catch { /* handled */ }
  };

  const handleSave = async () => {
    try {
      await loteService.save(formData);
      setOpenDialog(false);
      await loadData();
    } catch { /* handled */ }
  };

  const handleEdit = (lote: Lote) => { setFormData(lote); setOpenDialog(true); };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar este lote?')) return;
    try { await loteService.delete(id); await loadData(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'numeroLote', headerName: 'N° Lote', width: 130 },
    { field: 'articuloCodigo', headerName: 'Código Art.', width: 120 },
    { field: 'articuloDescripcion', headerName: 'Artículo', flex: 1, minWidth: 200 },
    { field: 'cantidadActual', headerName: 'Cant. Actual', width: 120, type: 'number' },
    { field: 'fechaVencimiento', headerName: 'Vencimiento', width: 120,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '-',
      renderCell: (params) => {
        if (!params.value) return '-';
        const fecha = new Date(params.value);
        const hoy = new Date();
        const diffDays = Math.ceil((fecha.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
        return <Chip label={fecha.toLocaleDateString()} size="small" color={diffDays <= 30 ? 'error' : diffDays <= 60 ? 'warning' : 'default'} />;
      }
    },
    { field: 'activo', headerName: 'Estado', width: 100,
      renderCell: (params) => <Chip label={params.value ? 'Activo' : 'Inactivo'} size="small" color={params.value ? 'success' : 'default'} />
    },
    { field: 'actions', headerName: 'Acciones', width: 120, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={() => handleEdit(params.row)}>Editar</Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.idLote)}>Eliminar</Button>
        </Box>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Gestión de Lotes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData(initialLoteState); setOpenDialog(true); }}>
          Nuevo Lote
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Lotes (${lotes.length})`} />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" fontSize="small" />Próximos a Vencer ({proximosVencer.length})
        </Box>} />
      </Tabs>

      {tabValue === 0 && (
        <Paper sx={{ height: 500 }}>
          <DataGrid rows={lotes} columns={columns} getRowId={(row) => row.idLote}
            loading={loading} density="compact"
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 2 }}>
          {proximosVencer.length === 0 ? (
            <Alert severity="success">No hay lotes próximos a vencer en los próximos 30 días</Alert>
          ) : (
            proximosVencer.map((l) => (
              <Alert key={l.idLote} severity="warning" sx={{ mb: 1 }}>
                Lote <strong>{l.numeroLote}</strong> ({l.articuloDescripcion}) - Vence: {l.fechaVencimiento ? new Date(l.fechaVencimiento).toLocaleDateString() : 'N/A'} - Cantidad: {l.cantidadActual}
              </Alert>
            ))
          )}
        </Paper>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{formData.idLote ? 'Editar' : 'Nuevo'} Lote</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="N° Lote" value={formData.numeroLote}
                onChange={(e) => setFormData({ ...formData, numeroLote: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="ID Artículo" type="number" value={formData.idArticulo || ''}
                onChange={(e) => setFormData({ ...formData, idArticulo: Number(e.target.value) })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Descripción" value={formData.descripcion ?? ''}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Fecha Fabricación" type="date"
                value={formData.fechaFabricacion ?? ''} InputLabelProps={{ shrink: true }}
                onChange={(e) => setFormData({ ...formData, fechaFabricacion: e.target.value || null })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Fecha Vencimiento" type="date"
                value={formData.fechaVencimiento ?? ''} InputLabelProps={{ shrink: true }}
                onChange={(e) => setFormData({ ...formData, fechaVencimiento: e.target.value || null })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Cantidad Inicial" type="number"
                value={formData.cantidadInicial ?? ''}
                onChange={(e) => setFormData({ ...formData, cantidadInicial: Number(e.target.value) })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observaciones" multiline rows={2}
                value={formData.observaciones ?? ''}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LotesPage;
